/**
 * src/offline/SyncStatusBar.tsx — WO-M3
 *
 * Minimal "pending sync" indicator.
 *
 * Shows a banner when there are captures waiting to be uploaded, or when
 * a sync is in progress. Provides a "Sync now" button.
 *
 * Renders nothing when online with no pending/failed captures.
 *
 * WO-M0 — design-system refactor. Uses theme.status tones for each state:
 *   syncing  → sent (blue/info)
 *   offline  → draft (neutral)
 *   pending  → inProgress (amber/warning)
 *   failed   → overdue (red/error)
 *
 * NEVER imports @smartbox/* (CLAUDE.md invariant 1).
 */

import { View, StyleSheet, ActivityIndicator } from 'react-native';
import type { ConnectivityState } from './useConnectivity';
import { Text, Button } from '../components/ui';
import { theme } from '../theme';

interface SyncStatusBarProps
  extends Pick<
    ConnectivityState,
    'isOnline' | 'isSyncing' | 'syncNow' | 'pendingCount' | 'failedCount'
  > {}

export function SyncStatusBar({
  isOnline,
  isSyncing,
  syncNow,
  pendingCount,
  failedCount,
}: SyncStatusBarProps) {
  // Nothing to show when fully synced and online.
  if (isOnline && pendingCount === 0 && failedCount === 0 && !isSyncing) {
    return null;
  }

  const hasFailures = failedCount > 0;
  const isOffline = !isOnline;

  // Determine label and tone.
  let label: string;
  let barStyle: object;
  let textColor: string;

  if (isSyncing) {
    label = 'Syncing captures…';
    barStyle = styles.barSyncing;
    textColor = theme.status.sent.fg;
  } else if (isOffline && pendingCount > 0) {
    label = `${pendingCount} capture${pendingCount !== 1 ? 's' : ''} queued — offline`;
    barStyle = styles.barOffline;
    textColor = theme.color.text.secondary;
  } else if (hasFailures) {
    label = `${failedCount} capture${failedCount !== 1 ? 's' : ''} failed to sync`;
    barStyle = styles.barFailed;
    textColor = theme.status.overdue.fg;
  } else if (pendingCount > 0) {
    label = `${pendingCount} capture${pendingCount !== 1 ? 's' : ''} pending sync`;
    barStyle = styles.barPending;
    textColor = theme.status.inProgress.fg;
  } else {
    return null;
  }

  return (
    <View
      style={[styles.bar, barStyle]}
      accessibilityRole="none"
      accessibilityLabel={label}
      accessibilityLiveRegion="polite"
    >
      {isSyncing ? (
        <ActivityIndicator
          size="small"
          color={theme.status.sent.fg}
          style={styles.spinner}
          accessibilityElementsHidden
        />
      ) : null}
      <Text variant="small" style={[styles.labelText, { color: textColor }]}>
        {label}
      </Text>
      {!isSyncing && isOnline ? (
        <Button
          title="Sync now"
          variant={hasFailures ? 'danger' : 'primary'}
          size="sm"
          fullWidth={false}
          onPress={syncNow}
          accessibilityLabel="Sync now"
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.sm,
    gap: theme.spacing.sm,
  },
  barSyncing: {
    backgroundColor: theme.status.sent.bg,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  barOffline: {
    backgroundColor: theme.color.bg.subtle,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  barPending: {
    backgroundColor: theme.status.inProgress.bg,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  barFailed: {
    backgroundColor: theme.status.overdue.bg,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  spinner: {
    flexShrink: 0,
  },
  labelText: {
    flex: 1,
  },
});
